/**
 * Global "register with a PRO / SoundExchange / MLC" nudge banner.
 *
 * Appears in Layout for authenticated artists when the backend
 * /publishing/me endpoint says any of show_pro_nudge,
 * show_mlc_nudge, or show_soundexchange_nudge is true.
 *
 * Pure educational/data-capture surface — clicking the CTA routes
 * to /royalties where the artist can finish setup.
 *
 * Dismissal is per-nudge and persists for 7 days in localStorage so
 * the user isn't pestered every page load.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'ov_publishing_nudge_dismissed_at';
const DISMISS_DAYS = 7;

function dismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function PublishingNudgeBanner() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null); // { kind, headline, body }
  const [dismissed, setDismissed] = useState(dismissedRecently());

  useEffect(() => {
    if (!token || !user || dismissed) return;
    // Only show for artists/producers — fans never get this banner.
    const isCreator = user?.is_artist || user?.is_producer || user?.is_ai_artist;
    if (!isCreator) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/publishing/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const d = res.data || {};
        // Priority: PRO > SoundExchange > MLC (worst money loss first)
        if (d.show_pro_nudge) {
          setActive({
            kind: 'pro',
            headline: 'Register with a PRO — collect your songwriter royalties',
            body: 'You haven\'t told us your BMI/ASCAP/SESAC membership. Without it, every radio play, venue, and Twitch stream pays $0 in songwriter royalties.',
          });
        } else if (d.show_soundexchange_nudge) {
          setActive({
            kind: 'soundexchange',
            headline: 'You\'re over 100 plays — claim SoundExchange royalties',
            body: 'SoundExchange has $250M+ unclaimed for indie artists. Registration is free and we\'ll keep your payee # on file.',
          });
        } else if (d.show_mlc_nudge) {
          setActive({
            kind: 'mlc',
            headline: 'Claim your MLC mechanical royalties',
            body: 'The MLC currently holds $400M+ in unmatched US streaming royalties. Register once — get paid forever.',
          });
        }
      } catch {
        // Silent — banner is non-critical.
      }
    })();
    return () => { cancelled = true; };
  }, [token, user, dismissed]);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* localStorage unavailable */ }
    setDismissed(true);
    setActive(null);
  };

  if (!active) return null;

  return (
    <div
      className="md:ml-48 px-4 py-2.5 bg-gradient-to-r from-[#1a1102] via-[#2a1d04] to-[#1a1102] border-b border-[#FFDA33]/30"
      data-testid={`publishing-nudge-${active.kind}`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center gap-3">
        <Sparkles size={16} className="text-[#FFDA33] shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0">
          <div className="text-[#FFDA33] font-mono text-[11px] uppercase tracking-wider truncate">
            {active.headline}
          </div>
          <div className="text-[#ccc] font-mono text-[11px] truncate hidden sm:block">
            {active.body}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/royalties')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FFDA33] text-black font-mono text-[11px] uppercase tracking-wider hover:bg-[#fff168] transition-colors whitespace-nowrap"
          data-testid="publishing-nudge-cta"
        >
          Set up <ArrowRight size={12} />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1.5 text-[#888] hover:text-white"
          aria-label="Dismiss for 7 days"
          data-testid="publishing-nudge-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

export default PublishingNudgeBanner;
